'use client';

import type { ReactNode } from 'react';
import { HStack, VStack } from '@terra/ui-styled-system';
import { MonoDefault, MonoSmall } from '../../../../../components/typography';
import { renderHttpMethodText } from '../../../../../utils/http-badge.utils';
import type {
  ChatObjectNavigationTarget,
  OnOpenChatObject,
} from '../../hitl-types';
import { resolveListTableColumns } from './data-query-columns.utils';
import {
  mapDataQueryRows,
  type DataQueryListItem,
} from './data-query-list-rows.utils';
import {
  DataQueryColumnHeader,
  DataQueryPanel,
  useDataQueryColumnLabels,
} from './data-query-table';

export type { DataQueryListItem } from './data-query-list-rows.utils';
export { mapDataQueryRows } from './data-query-list-rows.utils';

function DataQueryListRow({
  item,
  onOpenObject,
}: {
  item: DataQueryListItem;
  onOpenObject?: OnOpenChatObject;
}) {
  const canOpen = Boolean(item.navTarget && onOpenObject);

  return (
    <HStack
      gap={1.5}
      alignItems="center"
      py={1}
      px={1.5}
      borderRadius="sm"
      cursor={canOpen ? 'pointer' : 'default'}
      _hover={canOpen ? { bg: 'bg.subtle' } : undefined}
      onClick={
        canOpen && item.navTarget
          ? () => onOpenObject?.(item.navTarget as ChatObjectNavigationTarget)
          : undefined
      }
    >
      {item.method ? renderHttpMethodText(item.method) : null}
      <MonoDefault
        color="fg.default"
        truncate
        flex={1}
        textDecoration={canOpen ? undefined : 'none'}
        _hover={canOpen ? { textDecoration: 'underline' } : undefined}
      >
        {item.label}
      </MonoDefault>
      {item.badge ? (
        <MonoSmall color="fg.muted" flexShrink={0}>
          {item.badge}
        </MonoSmall>
      ) : null}
    </HStack>
  );
}

export function DataQueryListRows({
  collection,
  rows,
  onOpenObject,
  maxHeight = '260px',
  fallback,
}: {
  collection?: string;
  rows: unknown[];
  onOpenObject?: OnOpenChatObject;
  maxHeight?: string;
  fallback?: ReactNode;
}) {
  const items = mapDataQueryRows({ collection, rows });
  const columnLabels = useDataQueryColumnLabels();

  if (items.length === 0) {
    return fallback ?? null;
  }

  return (
    <DataQueryPanel>
      <VStack
        alignItems="stretch"
        gap={0.5}
        width="100%"
        maxHeight={maxHeight}
        overflowY="auto"
        overflowX="hidden"
      >
        <DataQueryColumnHeader
          columns={resolveListTableColumns({
            collection,
            labels: columnLabels,
          })}
        />
        {items.map((item) => (
          <DataQueryListRow
            key={item.key}
            item={item}
            onOpenObject={onOpenObject}
          />
        ))}
      </VStack>
    </DataQueryPanel>
  );
}
